import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';

/**
 * Get translated initiative status
 */
export function getInitiativeStatus(status: string, locale: Locale): string {
  const statusMap: Record<string, string> = {
    active: t(locale, 'statusActive'),
    inactive: t(locale, 'statusInactive'),
    draft: t(locale, 'statusDraft'),
    incoming: t(locale, 'statusIncoming'),
    funded: t(locale, 'statusFunded'),
    published: t(locale, 'statusPublished'),
    paused: t(locale, 'statusPaused'),
    pending_approval: t(locale, 'statusPendingApproval'),
    completed: t(locale, 'statusCompleted'),
    expired: t(locale, 'statusExpired'),
    pending: t(locale, 'statusPending'),
    cancelled: t(locale, 'statusCancelled'),
    pending_municipality_approval: t(locale, 'statusPendingMunicipalityApproval'),
    municipality_approved: t(locale, 'statusMunicipalityApproved'),
    municipality_rejected: t(locale, 'statusMunicipalityRejected'),
    pending_admin_approval: t(locale, 'statusPendingAdminApproval'),
    admin_approved: t(locale, 'statusAdminApproved'),
    planting_in_progress: t(locale, 'statusPlantingInProgress'),
  };

  return statusMap[status.toLowerCase()] || status;
}

/**
 * Get translated initiative type
 */
export function getInitiativeType(type: string, locale: Locale): string {
  const typeMap: Record<string, string> = {
    walk: t(locale, 'typeWalk'),
    walk_initiative: t(locale, 'typeWalk'),
    juthoor: t(locale, 'typeJuthoor'),
    juthoor_initiative: t(locale, 'typeJuthoor'),
  };

  return typeMap[type.toLowerCase()] || type;
}

/**
 * Get status badge color classes
 */
export function getStatusBadgeColor(status: string): string {
  const colorMap: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-400',
    incoming: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    pending_approval: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    funded: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    published: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    completed: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    pending_municipality_approval: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    municipality_approved: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    municipality_rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    pending_admin_approval: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    admin_approved: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    planting_in_progress: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  };

  return colorMap[status.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
}
